/**
 * userSignatureProvider — Amplify-Storage-backed read/write of the
 * BP's reusable signature PNG.
 *
 * Wraps `Storage.put` and `Storage.get` for the legacy
 * `resident-face-sheet/public/signatures/{file}` location. Amplify
 * applies the `public/` prefix automatically when `level: 'public'`
 * is set, matching the location the read-path used by
 * `OngoingAssessmentSection` already hits (it requests
 * `public/signatures/{file}` via the backend proxy).
 *
 * This is a thin driven provider — no business logic, no naming
 * conventions. The user-signature controller owns the filename pattern
 * and the Cognito-attribute write that pairs with this upload.
 */
import type { Storage as StorageClass } from "aws-amplify";

const BUCKET = "resident-face-sheet";
const FOLDER = "signatures";

export interface IUserSignatureProvider {
  /**
   * Upload a signature PNG. `fileName` is just the leaf name (no
   * `signatures/` prefix); the provider applies the folder.
   *
   * Returns the uploaded key as Amplify reports it (typically
   * `signatures/{fileName}`). Throws on network / permission failure.
   */
  uploadSignature: (args: { fileName: string; png: Blob }) => Promise<string>;
}

export const UserSignatureProviderFactory = ({
  Storage,
}: {
  Storage: typeof StorageClass;
}): IUserSignatureProvider => ({
  uploadSignature: async ({ fileName, png }) => {
    // `level: 'public'` puts the object under the `public/` prefix —
    // matches the read path the assessment view uses
    // (`public/signatures/{file}`). The bucket override mirrors
    // legacy `Storage.put('signatures/...', blob, { bucket })`.
    const result = (await Storage.put(`${FOLDER}/${fileName}`, png, {
      bucket: BUCKET,
      contentType: "image/png",
      level: "public",
    })) as { key?: string } | string;
    if (typeof result === "string") return result;
    return result.key ?? `${FOLDER}/${fileName}`;
  },
});
